/**
 * Status System V2 - Definitions and Helpers
 *
 * Three-track status system:
 * 1. Assignment Status - Global assignment progress (9 statuses)
 * 2. TG Status - Per-subcontractor work (7) + invoice (2)
 * 3. Customer Status - Communication (4) + invoice (2)
 */

import type {
  AssignmentStatusV2,
  AssignmentStatusConfig,
  TgWorkStatus,
  TgWorkStatusConfig,
  TgInvoiceStatus,
  TgInvoiceStatusConfig,
  CustomerCommStatus,
  CustomerCommStatusConfig,
  CustomerInvoiceStatus,
  CustomerInvoiceStatusConfig
} from '../types/statusTypes'

// =============================================
// ASSIGNMENT STATUS DEFINITIONS (9)
// =============================================

export const ASSIGNMENT_STATUSES_V2: AssignmentStatusConfig[] = [
  {
    key: 'ohanterad',
    name: 'Ohanterad',
    color: '#6B7280',
    bgColor: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
    description: 'Nytt uppdrag, ej planerat'
  },
  {
    key: 'planering',
    name: 'Planering',
    color: '#3B82F6',
    bgColor: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
    description: 'Under planering (trädgårdsmästare/datum saknas)'
  },
  {
    key: 'preliminar',
    name: 'Preliminär',
    color: '#EAB308',
    bgColor: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
    description: 'Preliminär planering, väntar bekräftelse'
  },
  {
    key: 'planerad',
    name: 'Planerad',
    color: '#06B6D4',
    bgColor: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900 dark:text-cyan-200',
    description: 'Bekräftad planering, väntar genomförande'
  },
  {
    key: 'pagaende',
    name: 'Pågående',
    color: '#F97316',
    bgColor: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
    description: 'Under genomförande'
  },
  {
    key: 'granskning',
    name: 'Granskning',
    color: '#8B5CF6',
    bgColor: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
    description: 'Alla trädgårdsmästare rapporterat, väntar attestering'
  },
  {
    key: 'attesterad',
    name: 'Attesterad',
    color: '#6366F1',
    bgColor: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200',
    description: 'Allt arbete godkänt, väntar fakturering'
  },
  {
    key: 'slutford',
    name: 'Slutförd',
    color: '#10B981',
    bgColor: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
    description: 'Helt klar - kund fakturerad, trädgårdsmästarfakturor mottagna'
  },
  {
    key: 'avbruten',
    name: 'Avbruten',
    color: '#EF4444',
    bgColor: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
    description: 'Avbrutet uppdrag'
  }
]

// =============================================
// TG WORK STATUS DEFINITIONS (7)
// =============================================

export const TG_WORK_STATUSES: TgWorkStatusConfig[] = [
  {
    key: 'preliminar_tilldelad',
    name: 'Preliminärt tilldelad',
    color: '#6B7280',
    bgColor: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
    description: 'Kopplad till preliminärt uppdrag'
  },
  {
    key: 'tilldelad',
    name: 'Tilldelad',
    color: '#3B82F6',
    bgColor: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
    description: 'Admin bekräftat planering'
  },
  {
    key: 'ej_rapporterad',
    name: 'Ej rapporterad',
    color: '#EAB308',
    bgColor: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
    description: 'Uppdragsdatum inträffat, väntar rapport'
  },
  {
    key: 'rapporterad',
    name: 'Rapporterad',
    color: '#06B6D4',
    bgColor: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900 dark:text-cyan-200',
    description: 'Rapport inskickad, väntar granskning'
  },
  {
    key: 'komplettering',
    name: 'Komplettering',
    color: '#F97316',
    bgColor: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
    description: 'Admin begärt komplettering'
  },
  {
    key: 'godkand',
    name: 'Godkänd',
    color: '#10B981',
    bgColor: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
    description: 'Rapport attesterad'
  },
  {
    key: 'avsagt',
    name: 'Avsagt',
    color: '#EF4444',
    bgColor: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
    description: 'Avsagt sig (sjuk/förhindrad)'
  }
]

// =============================================
// TG INVOICE STATUS DEFINITIONS (2)
// =============================================

export const TG_INVOICE_STATUSES: TgInvoiceStatusConfig[] = [
  {
    key: 'ej_fakturerad',
    name: 'Ej fakturerad',
    color: '#6B7280',
    bgColor: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
    description: 'Har inte skickat faktura'
  },
  {
    key: 'faktura_mottagen',
    name: 'Faktura mottagen',
    color: '#10B981',
    bgColor: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
    description: 'Faktura registrerad, kan kontrolleras mot underlag'
  }
]

// =============================================
// CUSTOMER COMM STATUS DEFINITIONS (4)
// =============================================

export const CUSTOMER_COMM_STATUSES: CustomerCommStatusConfig[] = [
  {
    key: 'ej_kontaktad',
    name: 'Ej kontaktad',
    color: '#6B7280',
    bgColor: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
    description: 'Kunden ej informerad'
  },
  {
    key: 'informerad',
    name: 'Informerad',
    color: '#3B82F6',
    bgColor: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
    description: 'Kunden informerad om uppdrag'
  },
  {
    key: 'bekraftad',
    name: 'Bekräftad',
    color: '#10B981',
    bgColor: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
    description: 'Kunden bekräftat tid'
  },
  {
    key: 'avbokad',
    name: 'Avbokad',
    color: '#EF4444',
    bgColor: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
    description: 'Kunden avbokat uppdraget'
  }
]

// =============================================
// CUSTOMER INVOICE STATUS DEFINITIONS (2)
// =============================================

export const CUSTOMER_INVOICE_STATUSES: CustomerInvoiceStatusConfig[] = [
  {
    key: 'ej_fakturerad',
    name: 'Ej fakturerad',
    color: '#6B7280',
    bgColor: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
    description: 'Faktura ej skapad i ekonomisystem'
  },
  {
    key: 'fakturerad',
    name: 'Fakturerad',
    color: '#10B981',
    bgColor: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
    description: 'Faktura skapad i ekonomisystemet'
  }
]

// =============================================
// HELPER FUNCTIONS - ASSIGNMENT STATUS
// =============================================

export function getAssignmentStatusV2(key: AssignmentStatusV2): AssignmentStatusConfig | undefined {
  return ASSIGNMENT_STATUSES_V2.find(s => s.key === key)
}

export function getAssignmentStatusNameV2(key: AssignmentStatusV2): string {
  return getAssignmentStatusV2(key)?.name || key
}

export function getAssignmentStatusColorV2(key: AssignmentStatusV2): string {
  return getAssignmentStatusV2(key)?.color || '#6B7280'
}

export function getAssignmentStatusBadgeClassesV2(key: AssignmentStatusV2): string {
  return getAssignmentStatusV2(key)?.bgColor || 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
}

// =============================================
// HELPER FUNCTIONS - TG WORK STATUS
// =============================================

export function getTgWorkStatus(key: TgWorkStatus): TgWorkStatusConfig | undefined {
  return TG_WORK_STATUSES.find(s => s.key === key)
}

export function getTgWorkStatusName(key: TgWorkStatus): string {
  return getTgWorkStatus(key)?.name || key
}

export function getTgWorkStatusColor(key: TgWorkStatus): string {
  return getTgWorkStatus(key)?.color || '#6B7280'
}

export function getTgWorkStatusBadgeClasses(key: TgWorkStatus): string {
  return getTgWorkStatus(key)?.bgColor || 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
}

// =============================================
// HELPER FUNCTIONS - TG INVOICE STATUS
// =============================================

export function getTgInvoiceStatus(key: TgInvoiceStatus): TgInvoiceStatusConfig | undefined {
  return TG_INVOICE_STATUSES.find(s => s.key === key)
}

export function getTgInvoiceStatusName(key: TgInvoiceStatus): string {
  return getTgInvoiceStatus(key)?.name || key
}

export function getTgInvoiceStatusBadgeClasses(key: TgInvoiceStatus): string {
  return getTgInvoiceStatus(key)?.bgColor || 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
}

// =============================================
// HELPER FUNCTIONS - CUSTOMER COMM STATUS
// =============================================

export function getCustomerCommStatus(key: CustomerCommStatus): CustomerCommStatusConfig | undefined {
  return CUSTOMER_COMM_STATUSES.find(s => s.key === key)
}

export function getCustomerCommStatusName(key: CustomerCommStatus): string {
  return getCustomerCommStatus(key)?.name || key
}

export function getCustomerCommStatusColor(key: CustomerCommStatus): string {
  return getCustomerCommStatus(key)?.color || '#6B7280'
}

export function getCustomerCommStatusBadgeClasses(key: CustomerCommStatus): string {
  return getCustomerCommStatus(key)?.bgColor || 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
}

// =============================================
// HELPER FUNCTIONS - CUSTOMER INVOICE STATUS
// =============================================

export function getCustomerInvoiceStatus(key: CustomerInvoiceStatus): CustomerInvoiceStatusConfig | undefined {
  return CUSTOMER_INVOICE_STATUSES.find(s => s.key === key)
}

export function getCustomerInvoiceStatusName(key: CustomerInvoiceStatus): string {
  return getCustomerInvoiceStatus(key)?.name || key
}

export function getCustomerInvoiceStatusBadgeClasses(key: CustomerInvoiceStatus): string {
  return getCustomerInvoiceStatus(key)?.bgColor || 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
}

// =============================================
// STATUS MAPPING (OLD -> NEW)
// =============================================

export const OLD_TO_NEW_STATUS_MAP: Record<string, AssignmentStatusV2> = {
  'ej_tilldelad': 'ohanterad',
  'planerad_ej_tilldelad': 'planering',
  'tilldelad_ej_planerad': 'planering',
  'preliminar_planering': 'preliminar',
  'planerad': 'planerad',
  'genomforande_rapportering': 'pagaende',
  'komplettering_rapportering': 'granskning',
  'attest': 'attesterad',
  'slutford': 'slutford',
  'avbrutten': 'avbruten'
}

/**
 * Convert old status to new V2 status
 */
export function migrateStatusToV2(oldStatus: string): AssignmentStatusV2 {
  return OLD_TO_NEW_STATUS_MAP[oldStatus] || 'ohanterad'
}

// =============================================
// CONTACT METHOD LABELS
// =============================================

export const CONTACT_METHOD_LABELS: Record<string, string> = {
  'sms': 'SMS',
  'phone': 'Telefon',
  'email': 'E-post',
  'in_person': 'Personligt möte'
}

export function getContactMethodLabel(method: string | null): string {
  if (!method) return '-'
  return CONTACT_METHOD_LABELS[method] || method
}

// =============================================
// STATUS FLOW VALIDATION
// =============================================

/**
 * Check if assignment can be invoiced (requires attesterad status)
 */
export function canInvoiceCustomer(assignmentStatus: AssignmentStatusV2): boolean {
  return assignmentStatus === 'attesterad' || assignmentStatus === 'slutford'
}

/**
 * Check if TG invoice can be registered (requires godkand status)
 */
export function canRegisterTgInvoice(tgStatus: TgWorkStatus): boolean {
  return tgStatus === 'godkand'
}

/**
 * Check if date change requires rebooking dialog
 */
export function requiresRebookingDialog(assignmentStatus: AssignmentStatusV2): boolean {
  return ['planerad', 'pagaende', 'granskning', 'attesterad'].includes(assignmentStatus)
}

/**
 * Check if TG can decline assignment
 */
export function canTgDecline(tgStatus: TgWorkStatus, assignmentStatus: AssignmentStatusV2): boolean {
  // Can only decline before assignment is pagaende
  const validTgStatuses: TgWorkStatus[] = ['preliminar_tilldelad', 'tilldelad']
  const validAssignmentStatuses: AssignmentStatusV2[] = ['preliminar', 'planerad']

  return validTgStatuses.includes(tgStatus) && validAssignmentStatuses.includes(assignmentStatus)
}
